import React, { useState, useEffect, useRef } from 'react';
import { calculateHexagram } from './utils/calculateHexagram';
import AIAnalysis from './components/AIAnalysis';
import PhoneInput from './components/PhoneInput';
import './App.css';
import { calculateEnergyPairs } from './utils/calculateEnergyPairs';
import EnergySection from './components/EnergySection';
import findDominantEnergy from './utils/findDominantEnergy';
import getNumberStyle from './utils/getNumberStyle';
import getEnergyStyle from './utils/getEnergyStyle';
import getHexagramStyle from './utils/getHexagramStyle';
import { generatePrompt as generateContemplationPrompt } from './utils/aiPrompt';
import { generatePrompt as generateBusinessPrompt } from './utils/aiPromptKD';
import { generatePrompt as generateRoleplayPrompt } from './utils/aiPromptRoleplay';
import { generatePrompt as generatePlanPrompt } from './utils/aiPromptPlan';
import ContactSection from './components/ContactSection';
import './ScrollToTopButton.css';
import AppFooter from './components/AppFooter';
import AuthModal from './components/AuthModal';
import { auth, signOutUser, getUserAnalysisCount, getUserData, doc, updateDoc, db } from '/src/firebase.js';
import ProfilePage from './components/ProfilePage';
import NumberMeanings from './components/NumberMeanings';
import HexagramInfo from './components/HexagramInfo';
import UpgradeModal from './components/UpgradeModal';
import UpgradeBanner from './components/UpgradeBanner';

function App() {
  // State variables for managing the application's data and UI
  const [result, setResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [resetAnalysis, setResetAnalysis] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [energyPairs, setEnergyPairs] = useState([]);
  const [dominantEnergy, setDominantEnergy] = useState('');
  const [promptContent, setPromptContent] = useState('');
  const [combinedPairs, setCombinedPairs] = useState([]);
  const [notificationList, setNotificationList] = useState([]);
  const [ghiChuList, setGhiChuList] = useState([]);
  const [warningNotice, setWarningNotice] = useState('');
  const [gender, setGender] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [concern, setConcern] = useState('');
  const [job, setJob] = useState('');
  const [businessContext, setBusinessContext] = useState('');
  const [interactionGoal, setInteractionGoal] = useState('');
  const [category, setCategory] = useState('Đọc vị');
  // Control visibility of the AI model selection here
  const [showNumberMeanings, setShowNumberMeanings] = useState(false); // Change default to false
  const [showAIModelSelection, setShowAIModelSelection] = useState(false); // Control AI model selection visibility
  const [selectedModel, setSelectedModel] = useState('qwen'); // Default model 'openai' , 'claude' , 'gemini' ,  'deepseek'
  const [isAdmin, setIsAdmin] = useState(false); // Track admin status
  const [userRole, setUserRole] = useState('regular');
  const [selectedSubtitle, setSelectedSubtitle] = useState((isAdmin || userRole === 'pioneer') ? 'business' : 'contemplation');

  // Control visibility of the AI Analysis section
  const showAIAnalysisSection = true; // Set to 'true' to show the AI analysis section, 'false' to hide it

  // Control visibility of the Prompt section here
  // Set to 'true' to show the Prompt section, 'false' to hide it
  const showPromptSection = true;

  // State to track if AI analysis has been completed
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  // Admin control for ContactSection visibility
  const adminShowContactSection = false; // Set to 'true' to always show, 'false' to hide

  // State for scroll-to-top button visibility
  const [showScrollButton, setShowScrollButton] = useState(false);
  
  // State for showing hexagram meanings
  const [selectedHexagrams, setSelectedHexagrams] = useState(new Set());

  // State for authentication modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [lastAnalysisTime, setLastAnalysisTime] = useState(null);
  const [maxAnalysisCount, setMaxAnalysisCount] = useState(9);

  // Function to handle phone number submission
  const handlePhoneSubmit = (number) => {
    setPhoneNumber(number);
    const hexagramResult = calculateHexagram(number);
    const calculatedEnergyPairs = calculateEnergyPairs(number);
    setEnergyPairs(calculatedEnergyPairs);
    setDominantEnergy(findDominantEnergy(calculatedEnergyPairs));
    setResult(hexagramResult);
    setShowResults(true);
    setResetAnalysis(true);
    setHasAnalyzed(false);

    // Select prompt generator based on category
    let generateSelectedPrompt;
    if (selectedSubtitle === 'business') {
      switch (category) {
        case 'Đọc vị':
          generateSelectedPrompt = generateBusinessPrompt;
          break;
        case 'Nhập vai':
          generateSelectedPrompt = generateRoleplayPrompt;
          break;
        case 'Lập kế hoạch':
          generateSelectedPrompt = generatePlanPrompt;
          break;
        default:
          generateSelectedPrompt = generateBusinessPrompt;
      }
    } else {
      generateSelectedPrompt = generateContemplationPrompt;
    }
    
    setPromptContent(generateSelectedPrompt(
      {...hexagramResult, phoneNumber: number, energyDetails: calculatedEnergyPairs}, 
      gender, 
      ageRange, 
      maritalStatus, 
      concern, 
      job,
      businessContext,
      interactionGoal,
      dominantEnergy,
      combinedPairs,
      notificationList,
      ghiChuList,
      warningNotice
    ));

    setTimeout(() => {
      // Scroll to different sections based on selectedSubtitle
      if (selectedSubtitle === 'business') {
        const aiSection = document.querySelector('.ai-section');
        if (aiSection) {
          aiSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        const hexagramSection = document.getElementById('hexagram-section');
        if (hexagramSection) {
          hexagramSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
  };

  // Function to handle changes in prompt-related data
  const handlePromptChange = (category, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice) => {
    if (result && phoneNumber) {
      // Select prompt generator based on category
      let generateSelectedPrompt;
      if (selectedSubtitle === 'business') {
        switch (category) {
          case 'Đọc vị':
            generateSelectedPrompt = generateBusinessPrompt;
            break;
          case 'Nhập vai':
            generateSelectedPrompt = generateRoleplayPrompt;
            break;
          case 'Lập kế hoạch':
            generateSelectedPrompt = generatePlanPrompt;
            break;
          default:
            generateSelectedPrompt = generateBusinessPrompt;
        }
      } else {
        generateSelectedPrompt = generateContemplationPrompt;
      }
      
      setPromptContent(generateSelectedPrompt(
        {...result, phoneNumber, energyDetails: energyPairs}, 
        gender, 
        ageRange, 
        maritalStatus, 
        concern, 
        job,
        businessContext,
        interactionGoal,
        dominantEnergy,
        combinedPairs,
        notificationList,
        ghiChuList,
        warningNotice
      ));
    }
  };

  // Function to handle energy data updates
  const handleEnergyData = (dominantEnergy, combinedPairs, notifications, ghiChu, warning, energyPairs) => {
    setCombinedPairs(combinedPairs);
    setDominantEnergy(dominantEnergy);
    setNotificationList(notifications);
    setGhiChuList(ghiChu);
    setWarningNotice(warning);
    setEnergyPairs(energyPairs);
    handlePromptChange(category, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notifications, ghiChu, warning);
  };

  // Function to handle AI analysis input changes
  const handleAIAnalysisChange = (category, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal) => {
    setCategory(category);
    setGender(gender);
    setAgeRange(ageRange);
    setMaritalStatus(maritalStatus);
    setConcern(concern);
    setJob(job);
    setBusinessContext(businessContext);
    setInteractionGoal(interactionGoal);
    handlePromptChange(category, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice);
  };

  // Function to handle AI model selection changes
  const handleModelChange = (model) => {
    setSelectedModel(model);
  };

  // Function to scroll to the top
  const scrollToTop = () => {
    const inputSection = document.querySelector('.input-section');
    if (inputSection) {
      inputSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 300; // Adjust this value as needed
      setShowScrollButton(scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to handle auth modal toggle
  const handleAuthToggle = () => {
    setShowAuthModal(!showAuthModal);
    setIsMenuOpen(false);
  };

  // Function to handle auth changes
  const handleAuthChange = () => {
    setUser(auth.currentUser);
  };

  // Function to handle sign out
  const handleSignOut = async () => {
    try {
      await signOutUser();
      setUser(null);
      setIsMenuOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Function to handle profile modal toggle
  const handleProfileToggle = () => {
    setShowProfileModal(!showProfileModal);
    setIsMenuOpen(false);
  };

  // Thêm hàm để xác định model dựa trên role
  const getDefaultModelForRole = (role) => {
    return 'qwen'; // Always return qwen
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      setUser(user);
      if (user) {
        const userData = await getUserData(user.uid);
        const analysisCount = userData?.analysisCount || 0;
        const maxAnalysisCount = userData?.maxAnalysisCount || 9;
        
        setAnalysisCount(analysisCount);
        setLastAnalysisTime(userData?.lastAnalysisTime || null);
        setMaxAnalysisCount(maxAnalysisCount);
        
        const isAdminUser = userData?.role === 'admin';
        const isPioneerUser = userData?.role === 'pioneer';
        const isVipUser = userData?.role === 'vip';
        
        // Cập nhật selectedSubtitle dựa trên role
        if (isAdminUser || isPioneerUser) {
          setSelectedSubtitle('business');
        } else {
          setSelectedSubtitle('contemplation');
        }
        
        // Kiểm tra nếu user VIP hoặc Pioneer đã hết lượt
        if ((isVipUser || isPioneerUser) && analysisCount >= maxAnalysisCount) {
          try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
              role: 'regular'
            });
            
            setUserRole('regular');
            setShowAIModelSelection(false);
            setSelectedModel('qwen');
            setSelectedSubtitle('contemplation');
          } catch (error) {
            console.error('Error updating user role:', error);
          }
        } else {
          setShowNumberMeanings(isAdminUser);
          setShowAIModelSelection(isAdminUser || isVipUser || isPioneerUser);
          setIsAdmin(isAdminUser);
          setUserRole(userData?.role || 'regular');
          
          if (!isAdminUser && !isVipUser && !isPioneerUser) {
            setSelectedModel('qwen');
          }
        }
      } else {
        // Reset state khi không có user
        setAnalysisCount(0);
        setLastAnalysisTime(null);
        setMaxAnalysisCount(9);
        setShowNumberMeanings(false);
        setSelectedModel('qwen');
        setUserRole('regular');
        setShowAIModelSelection(false);
        setSelectedSubtitle('contemplation');
      }
    });

    return () => unsubscribe();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const remainingAnalyses = maxAnalysisCount - analysisCount;

  const formattedLastAnalysisTime = lastAnalysisTime
    ? new Date(lastAnalysisTime.seconds * 1000).toLocaleString()
    : 'Chưa có';

  const handleAnalysisCountChange = (newCount) => {
    setAnalysisCount(newCount);
  };

  // Function to handle subtitle change
  const handleSubtitleChange = (subtitle) => {
    setSelectedSubtitle(subtitle);
    // Regenerate prompt with new subtitle
    if (result && phoneNumber) {
      let generateSelectedPrompt;
      if (subtitle === 'business') {
        switch (category) {
          case 'Đọc vị':
            generateSelectedPrompt = generateBusinessPrompt;
            break;
          case 'Nhập vai':
            generateSelectedPrompt = generateRoleplayPrompt;
            break;
          case 'Lập kế hoạch':
            generateSelectedPrompt = generatePlanPrompt;
            break;
          default:
            generateSelectedPrompt = generateBusinessPrompt;
        }
      } else {
        generateSelectedPrompt = generateContemplationPrompt;
      }

      setPromptContent(generateSelectedPrompt(
        {...result, phoneNumber, energyDetails: energyPairs}, 
        gender, 
        ageRange, 
        maritalStatus, 
        concern, 
        job,
        businessContext,
        interactionGoal,
        dominantEnergy,
        combinedPairs,
        notificationList,
        ghiChuList,
        warningNotice
      ));
    }
  };

  useEffect(() => {
    if (result && phoneNumber) {
      let generateSelectedPrompt;
      if (selectedSubtitle === 'business') {
        switch (category) {
          case 'Đọc vị':
            generateSelectedPrompt = generateBusinessPrompt;
            break;
          case 'Nhập vai':
            generateSelectedPrompt = generateRoleplayPrompt;
            break;
          case 'Lập kế hoạch':
            generateSelectedPrompt = generatePlanPrompt;
            break;
          default:
            generateSelectedPrompt = generateBusinessPrompt;
        }
      } else {
        generateSelectedPrompt = generateContemplationPrompt;
      }

      setPromptContent(generateSelectedPrompt(
        {...result, phoneNumber, energyDetails: energyPairs}, 
        gender, 
        ageRange, 
        maritalStatus, 
        concern, 
        job,
        businessContext,
        interactionGoal,
        dominantEnergy,
        combinedPairs,
        notificationList,
        ghiChuList,
        warningNotice
      ));
    }
  }, [result, phoneNumber, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice, selectedSubtitle, category, energyPairs]);

  return (
    <div className="app-container">
      <header className="header-section">
        <div className="header-container">
          <div className="header-top">
            <div className="logo-container">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 700 150">
                <g transform="translate(20, 20)">
                  <a href="/">
                    <text x="0" y="85" 
                          font-family="'Montserrat', sans-serif" 
                          font-weight="700" 
                          font-size="72" 
                          letter-spacing="-1"
                          fill="#ffffff">Luận</text>
                  </a>
                  <rect x="185" y="10" 
                        width="100" height="100" 
                        rx="20" 
                        fill="none"
                        stroke="#ffffff"
                        stroke-width="4"/>
                  <a href="/">
                    <text x="235" y="85" 
                          font-family="'Helvetica Neue', Arial, sans-serif" 
                          font-weight="900" 
                          font-size="72"
                          letter-spacing="-1"
                          text-anchor="middle"
                          fill="#6f01ff">Số</text>
                  </a>
                  <a href="/">
                    <text x="310" y="85" 
                          font-family="'Montserrat', sans-serif" 
                          font-weight="700" 
                          font-size="72" 
                          letter-spacing="-1"
                          fill="#ffffff">Điện Thoại</text>
                  </a>
                </g>
              </svg>
            </div>

            <div className="auth-section">
              <div className="mobile-menu-container">
                <button 
                  className="mobile-menu-button" 
                  onClick={toggleMenu}
                  aria-expanded={isMenuOpen}
                  aria-label="Menu"
                >
                  <div className="menu-icon">
                    <div className="bar"></div>
                    <div className="bar"></div>
                    <div className="bar"></div>
                  </div>
                </button>
                {isMenuOpen && (
                  <>
                    <div className={`menu-backdrop ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}></div>
                    <div className={`mobile-menu ${isMenuOpen ? 'active' : ''}`} ref={menuRef}>
                      {user ? (
                        <>
                          <div className="mobile-menu-header">
                            <button onClick={handleProfileToggle} className="mobile-menu-item">
                              <span className={`user-badge ${userRole}`}>
                                {userRole === 'admin' && <i className="fas fa-shield-alt"></i>}
                                {userRole === 'pioneer' && <i className="fas fa-rocket"></i>}
                                {userRole === 'vip' && <i className="fas fa-crown"></i>}
                                {userRole === 'admin' ? 'ADMIN' : 
                                 userRole === 'pioneer' ? 'PIONEER' :
                                 userRole === 'vip' ? 'VIP' : 
                                 'FREE'}
                              </span>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                              </svg>
                              {user.displayName || user.email}
                            </button>
                          </div>
                          <button onClick={handleSignOut} className="mobile-menu-item">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                              <polyline points="16 17 21 12 16 7"></polyline>
                              <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            Đăng xuất
                          </button>
                        </>
                      ) : (
                        <button onClick={handleAuthToggle} className="mobile-menu-item">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                            <polyline points="10 17 15 12 10 7"></polyline>
                            <line x1="15" y1="12" x2="3" y2="12"></line>
                          </svg>
                          Đăng nhập/Đăng ký
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="desktop-auth-button">
                {user ? (
                  <>
                    <div className="desktop-profile-section">
                      <div className="user-info" onClick={handleProfileToggle}>
                        <span className={`user-badge ${userRole}`}>
                          {userRole === 'admin' && <i className="fas fa-shield-alt"></i>}
                          {userRole === 'pioneer' && <i className="fas fa-rocket"></i>}
                          {userRole === 'vip' && <i className="fas fa-crown"></i>}
                          {userRole === 'admin' ? 'ADMIN' : 
                           userRole === 'pioneer' ? 'PIONEER' :
                           userRole === 'vip' ? 'VIP' : 
                           'FREE'}
                        </span>
                        <span className="user-name">{user.displayName || user.email}</span>
                      </div>
                      <button onClick={handleSignOut} className="auth-button-header">
                        Đăng xuất
                      </button>
                    </div>
                  </>
                ) : (
                  <button onClick={handleAuthToggle} className="auth-button-header">
                    Đăng nhập/Đăng ký
                  </button>
                )}
              </div>
            </div>
          </div>

          <h1 className="app-title">
            <span>Khám Phá Bí Mật Số Điện Thoại</span>
          </h1>
          <p className="app-subtitle" style={{ 
            fontSize: '1rem',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexDirection: 'column',
            gap: '8px',
            textAlign: 'center'
          }}>
            {/* Điều chỉnh text dựa trên role */}
            {(isAdmin || userRole === 'pioneer') ? (
              <span>Giải mã bí ẩn của <b>10 con số điện thoại</b> bằng sự tò mò để</span>
            ) : (
              <span>Giải mã bí ẩn của <b>10 con số điện thoại</b> bằng sự tò mò</span>
            )}
            {/* Chỉ admin và pioneer mới thấy được dropdown */}
            {(isAdmin || userRole === 'pioneer') ? (
              <select
                value={selectedSubtitle}
                className="subtitle-dropdown"
                onChange={(e) => handleSubtitleChange(e.target.value)}
              >
                <option value="contemplation">Chiêm nghiệm</option>
                <option value="business">Kinh doanh</option>
              </select>
            ) : null}
          </p>
        </div>
      </header>

      <main className="main-content">
        <section className="input-section">
          <PhoneInput onSubmit={handlePhoneSubmit} />
        </section>

        {showResults && result && (
          <>
            {selectedSubtitle !== 'business' && (
              <>
                <HexagramInfo
                  result={result}
                  phoneNumber={phoneNumber}
                  selectedHexagrams={selectedHexagrams}
                  setSelectedHexagrams={setSelectedHexagrams}
                  getHexagramStyle={getHexagramStyle}
                  getNumberStyle={getNumberStyle}
                  getEnergyStyle={getEnergyStyle}
                  userRole={userRole}
                />

                {showNumberMeanings && <NumberMeanings phoneNumber={phoneNumber} />}

                <EnergySection 
                  energyPairs={energyPairs} 
                  dominantEnergy={dominantEnergy} 
                  phoneNumber={phoneNumber} 
                  getHexagramStyle={getHexagramStyle}
                  onEnergyData={handleEnergyData}
                />
              </>
            )}
            {/* Conditionally render the AI analysis section based on showAIAnalysisSection variable */}
            {user ? (
              <section className="ai-section">
                <AIAnalysis 
                  hexagramData={{...result, phoneNumber, energyDetails: energyPairs}}
                  resetAnalysis={resetAnalysis} 
                  onResetComplete={() => setResetAnalysis(false)} 
                  phoneNumber={phoneNumber}
                  onPromptChange={handleAIAnalysisChange}
                  selectedModel={selectedModel}
                  onModelChange={handleModelChange}
                  showAIModelSelection={showAIModelSelection}
                  isAdmin={isAdmin}
                  dominantEnergy={dominantEnergy}
                  combinedPairs={combinedPairs}
                  notificationList={notificationList}
                  ghiChuList={ghiChuList}
                  warningNotice={warningNotice}
                  setHasAnalyzed={setHasAnalyzed}
                  onAnalysisCountChange={handleAnalysisCountChange}
                  maxAnalysisCount={maxAnalysisCount}
                  userRole={userRole}
                  selectedSubtitle={selectedSubtitle}
                />
              </section>
            ) : (
              <section className="ai-section">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
                    Hãy <button onClick={handleAuthToggle} className="auth-button-header">Đăng nhập/Đăng ký</button> MIỄN PHÍ để xem được Luận Giải Ý Nghĩa Số
                  </p>
                </div>
              </section>
            )}
            {/* Chỉ hiển thị Prompt section cho Admin và khi showPromptSection = true */}
            {isAdmin && showPromptSection && (
              <section className="prompt-section">
                <h2 className="section-title">Prompt</h2>
                <div className="prompt-content">
                  <pre>
                    <code>{promptContent}</code>
                  </pre>
                </div>
              </section>
            )}
            {/* Conditionally render the Contact section based on hasAnalyzed variable and admin control */}
            <ContactSection showContact={hasAnalyzed || adminShowContactSection} />
          </>
        )}
      </main>
      <AppFooter />
      {showScrollButton && (
        <button onClick={scrollToTop} className="scroll-to-top-button">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      )}
      {showAuthModal && (
        <AuthModal onClose={handleAuthToggle} onAuthChange={handleAuthChange} />
      )}
      {showProfileModal && (
        <ProfilePage onClose={handleProfileToggle} onAuthChange={handleAuthChange} />
      )}
    </div>
  );
}

export default App;
